import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FaSearch,
  FaEye,
  FaSyncAlt,
  FaCalendarAlt,
  FaExclamationCircle
} from "react-icons/fa";
import { fetchWithAuth } from "../../../utils/api";
import BookingDetailsModal from "./components/BookingDetailsModal";
import "./TopmateBookings.css";

function TopmateRecentBookings() {
  const [searchParams] = useSearchParams();

  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters & State
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [dateRange, setDateRange] = useState("all");
  const [page, setPage] = useState(1);

  // Modal state
  const [activeDetailsBooking, setActiveDetailsBooking] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        page: String(page),
        limit: "10",
        search: search.trim(),
        status,
        dateRange
      });

      const res = await fetchWithAuth(`/admin/topmate/bookings?${params.toString()}`);
      const data = await res.json();

      if (data.success) {
        setBookings(data.bookings || []);
        setPagination(data.pagination || { total: 0, page: 1, limit: 10, totalPages: 1 });
      } else {
        setError(data.message || "Failed to load bookings");
      }
    } catch (err) {
      setError("Network error fetching bookings data");
    } finally {
      setLoading(false);
    }
  }, [page, search, status, dateRange]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  return (
    <div className="topmate-dashboard-container">
      {/* HEADER */}
      <div className="topmate-header-card">
        <div className="topmate-header-text">
          <div className="topmate-header-badge">
            <FaCalendarAlt /> Session Bookings Management
          </div>
          <h1>Recent Bookings</h1>
          <p>
            Review Topmate 1:1 call appointments, verified session date/time (IST), customer metadata, and automated confirmation email delivery.
          </p>
        </div>

        <div className="topmate-header-actions">
          <button className="topmate-btn-secondary" onClick={fetchBookings} disabled={loading}>
            <FaSyncAlt className={loading ? "spin-icon" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* NOTIFICATIONS */}
      {error && (
        <div className="topmate-alert danger">
          <FaExclamationCircle /> <span>{error}</span>
        </div>
      )}

      {/* FILTERS AND CONTROLS */}
      <div className="filter-controls-bar">
        <div className="search-input-wrap">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="topmate-input"
            placeholder="Search customer, email, Topmate ID, Portfolio ID, service..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <select
          className="topmate-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Session Statuses</option>
          <option value="Confirmed">Confirmed</option>
          <option value="Completed">Completed</option>
          <option value="Rescheduled">Rescheduled</option>
          <option value="Cancelled">Cancelled</option>
        </select>

        <select
          className="topmate-select"
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Time (IST)</option>
          <option value="today">Today (IST)</option>
          <option value="7d">Last 7 Days (IST)</option>
          <option value="30d">Last 30 Days (IST)</option>
          <option value="90d">Last 90 Days (IST)</option>
          <option value="year">This Year (IST)</option>
        </select>
      </div>

      {/* BOOKINGS TABLE */}
      <div className="topmate-card">
        {loading ? (
          <div className="topmate-state-box">
            <div className="topmate-spinner" />
            <p>Loading session bookings...</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="topmate-state-box">
            <p>No session bookings match the selected filters.</p>
            <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
              Try clearing search terms or selecting "All Time".
            </span>
          </div>
        ) : (
          <>
            <div className="topmate-table-wrapper">
              <table className="topmate-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer Name</th>
                    <th>Customer Email</th>
                    <th>Booking Type</th>
                    <th>Service Name</th>
                    <th>Topmate ID</th>
                    <th>Portfolio Booking ID</th>
                    <th>Date & Time (IST)</th>
                    <th>Session Status</th>
                    <th>Email Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking, index) => {
                    const serialNumber = (pagination.page - 1) * pagination.limit + index + 1;
                    return (
                      <tr key={booking._id}>
                        <td>{serialNumber}</td>
                        <td>
                          <strong>{booking.customerName}</strong>
                        </td>
                        <td style={{ color: "#94a3b8" }}>{booking.customerEmail}</td>
                        <td>
                          <span style={{ fontSize: "0.75rem", background: "rgba(56, 189, 248, 0.12)", color: "#38bdf8", padding: "2px 8px", borderRadius: "6px" }}>
                            1:1 Call
                          </span>
                        </td>
                        <td>{booking.serviceName}</td>
                        <td>
                          <code style={{ fontSize: "0.75rem", background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: "4px" }}>
                            {booking.topmateBookingId}
                          </code>
                        </td>
                        <td>
                          <code style={{ fontSize: "0.75rem", background: "rgba(56,189,248,0.1)", color: "#38bdf8", padding: "2px 6px", borderRadius: "4px" }}>
                            {booking.portfolioBookingId}
                          </code>
                        </td>
                        <td style={{ color: "#38bdf8", fontWeight: 500 }}>{booking.bookingDateIST}</td>
                        <td>
                          <span className={`status-pill ${booking.status?.toLowerCase()}`}>{booking.status}</span>
                        </td>
                        <td>
                          {booking.emailSent ? (
                            <span className="status-pill sent">Sent</span>
                          ) : (
                            <span className="status-pill pending">
                              {booking.bookingDateIST === "Unavailable" ? "Pending Date" : "Pending"}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="table-actions-cell">
                            {/* View Full Details */}
                            <button
                              className="btn-action"
                              onClick={() => setActiveDetailsBooking(booking)}
                              title="View full booking metadata"
                            >
                              <FaEye /> View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            <div className="topmate-pagination">
              <div>
                Showing {(pagination.page - 1) * pagination.limit + 1} -{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
              </div>
              <div className="pagination-buttons">
                <button
                  className="btn-page"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span style={{ fontSize: "0.8rem", color: "#f8fafc" }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  className="btn-page"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* VIEW DETAILS MODAL */}
      {activeDetailsBooking && (
        <BookingDetailsModal
          booking={activeDetailsBooking}
          onClose={() => setActiveDetailsBooking(null)}
        />
      )}
    </div>
  );
}

export default TopmateRecentBookings;
